import isMobile from 'ismobilejs'
import _debounce from 'lodash/debounce'
import {
  FC,
  Fragment,
  MouseEvent,
  useCallback,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import ReactTextTransition from 'react-text-transition'
import { usePlayerState } from '../contexts/playerState'
import { playButtonSize } from './CustomControls'

// Single seek button
interface SeekButtonProps {
  value: number
  onClick: (e: MouseEvent) => void
  color?: string
  showInline?: boolean
  playerHeight?: number
  playerWidth?: number
}

const SEEK_STEP = 10

const SeekForward: FC<SeekButtonProps> = ({
  value,
  onClick,
  color,
  showInline = false,
  playerHeight,
  playerWidth,
}) => {
  const textValue = `+${value}s`
  const isMobileDevice = isMobile().phone

  return (
    <button
      role="button"
      aria-label={`Forward ${SEEK_STEP}s`}
      title={`Forward ${SEEK_STEP}s`}
      css={
        showInline
          ? inlineButtonStyle(isMobileDevice)
          : [
              seekButtonStyle(playerHeight || 0, playerWidth || 0),
              forwardButtonStyle,
            ]
      }
      className={`vjs-button vjs-seek-forward ${showInline && 'vjs-control'}`}
      onClick={onClick}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.0"
        viewBox="0 0 300 313"
        height="30"
      >
        <g fill={color || '#FFF'}>
          <path d="M135 305A146 146 0 0110 126 145 145 0 01169 20c4 0 3 1 5-10l2-8a1085 1085 0 0160 45l-72 22 1-9 1-10a114 114 0 1095 138l2-5a418 418 0 0128 5l-1 11a144 144 0 01-155 107z" />
          <path d="M176 201c-10-3-18-10-22-22l-1-21c-1-19 0-22 3-30 6-11 16-16 30-16 15 0 25 5 30 17 4 7 4 11 4 27s0 21-3 28c-6 12-15 18-31 18l-10-1zm17-16l5-3c3-4 4-8 4-22 1-19-1-26-6-30-3-3-8-4-12-3-11 2-14 8-14 30 0 18 3 25 10 28 3 2 10 2 13 0zM119 199l-1-33v-32a280 280 0 00-20 14c-2 0-9-8-9-10l2-3 25-19 4-3h7l8 1 1 43-1 43h-8l-8-1z" />
        </g>
      </svg>
      {!showInline && value !== 0 && (
        <ReactTextTransition
          text={textValue}
          style={{ margin: '0 4px' }}
          inline
          overflow
        />
      )}
    </button>
  )
}

const SeekBackward = ({
  value,
  onClick,
  color,
  showInline = false,
  playerHeight,
  playerWidth,
}: SeekButtonProps) => {
  const textValue = `${value}s`
  const isMobileDevice = isMobile().phone

  return (
    <button
      role="button"
      aria-label={`Backward ${SEEK_STEP}s`}
      title={`Backward ${SEEK_STEP}s`}
      css={
        showInline
          ? inlineButtonStyle(isMobileDevice)
          : [
              seekButtonStyle(playerHeight || 0, playerWidth || 0),
              backwardButtonStyle,
            ]
      }
      className={`vjs-button vjs-seek-backward ${showInline && 'vjs-control'}`}
      onClick={onClick}
    >
      {!showInline && value !== 0 && (
        <ReactTextTransition
          text={textValue}
          style={{ margin: '0 4px' }}
          inline
          overflow
        />
      )}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.0"
        viewBox="0 0 300 313"
        height="30"
      >
        <g fill={color || '#FFF'}>
          <path d="M133 305A145 145 0 019 187a680 680 0 0028-5l2 5a114 114 0 10210-83 115 115 0 00-115-55l1 10 1 9-72-22 60-45 2 8c2 11 1 10 5 10a145 145 0 01161 119 146 146 0 01-121 166c-8 2-30 2-38 1z" />
          <path d="M163 201c-10-3-18-10-22-22l-1-21c0-19 0-22 4-30 5-11 15-16 30-16 14 0 24 5 30 17 3 7 4 11 4 27s-1 21-4 28c-6 12-15 18-31 18l-10-1zm17-16c2 0 4-2 5-3 3-4 4-8 4-22 1-19 0-26-6-30-3-3-7-4-12-3-10 2-13 8-13 30 0 18 2 25 9 28 3 2 10 2 13 0zM107 199l-1-33v-32a280 280 0 00-20 14c-2 0-9-8-9-10l2-3 25-19 4-3h7l8 1 1 43-1 43h-8l-8-1z" />
        </g>
      </svg>
    </button>
  )
}

// Buttons component

interface SeekButtonsProps {
  color?: string
  step: number
}

const SeekButtonsDefaultProps = {
  player: null,
  color: '#FFF' as string,
  step: SEEK_STEP,
}

const SeekButtons: FC<SeekButtonsProps> = ({
  color,
  step = SEEK_STEP,
}) => {
  const {
    isInitialized,
    playerInstance: player,
    playerHeight,
    playerWidth,
  } = usePlayerState()

  const [secondsBackward, setSecondsBackward] = useState(0)
  const [secondsForward, setSecondsForward] = useState(0)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedBackwardsSeek = useCallback(
    _debounce((seconds: number) => {
      player?.currentTime(player.currentTime() + seconds)

      setTimeout(() => setSecondsBackward(0), 500)
    }, 500),
    [player?.currentTime, setSecondsBackward, _debounce],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSeekForward = useCallback(
    _debounce((seconds: number) => {
      player?.currentTime(player.currentTime() + seconds)

      setTimeout(() => setSecondsForward(0), 500)
    }, 500),
    [player?.currentTime, setSecondsForward, _debounce],
  )

  const onBackwardClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      setSecondsBackward(secondsBackward - step)
      debouncedBackwardsSeek(secondsBackward - step)
    },
    [secondsBackward, debouncedBackwardsSeek, step],
  )

  const onForwardClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      setSecondsForward(secondsForward + step)
      debouncedSeekForward(secondsForward + step)
    },
    [secondsForward, debouncedSeekForward, step],
  )

  if (!isInitialized || !player?.controlBar) return null

  const isMobileDevice = isMobile().phone

  return ReactDOM.createPortal(
    <Fragment>
      <SeekBackward
        value={secondsBackward}
        onClick={onBackwardClick}
        color={color}
        playerHeight={playerHeight}
        playerWidth={playerWidth}
        showInline={!isMobileDevice}
      />
      <SeekForward
        value={secondsForward}
        onClick={onForwardClick}
        color={color}
        playerHeight={playerHeight}
        playerWidth={playerWidth}
        showInline={!isMobileDevice}
      />
    </Fragment>,
    player.controlBar.el(),
  )
}

SeekButtons.defaultProps = SeekButtonsDefaultProps

export default SeekButtons

const seekButtonStyle = (playerHeight: number, playerWidth: number) => ({
  '&&': {
    position: 'absolute' as const,
    padding: '16px',
    bottom: `${playerHeight / 2}px`,
    left: `${playerWidth / 2}px`,
    fontSize: '12px',
    fontWeight: 'bold' as const,
    display: 'flex',
    alignItems: 'center',
    outline: 0,
    cursor: 'pointer',
    zIndex: 1,
  },
})

const forwardButtonStyle = {
  transform: `translate(calc(${playButtonSize} / 2), 50%)`,
}

const backwardButtonStyle = {
  transform: `translate(calc(-${playButtonSize} / 2 - 100%), 50%)`,
}

const inlineButtonStyle = (isMobileDevice: boolean) => ({
  cursor: 'pointer',
  '&:active': {
    transform: 'scale(0.9)',
  },
  svg: {
    height: '2em',
    margin: 0,
    width: '2em',
    filter: isMobileDevice
      ? 'unset'
      : 'drop-shadow( 0px 2px 4px rgba(0, 0, 0, .5))',
  },
})
